// src/components/cart/MiniCartDrawer.tsx
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartProvider";
import { useTranslation } from "react-i18next";
import { X, ShoppingBag, Minus, Plus, Trash2, ArrowRight } from "lucide-react";

/* ======================
   HELPERS
====================== */
function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

const RAW_API_URL =
  process.env.REACT_APP_API_URL || process.env.NEXT_PUBLIC_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");

function assetUrl(url?: string | null) {
  if (!url) return "/images/placeholder.jpg";
  const u = String(url).trim();

  if (u === "/images" || u === "/images/") return "/images/placeholder.jpg";
  if (/^https?:\/\//i.test(u)) return u;

  const normalized = u.split("\\").join("/");

  if (normalized.startsWith("/"))
    return API_URL ? `${API_URL}${normalized}` : normalized;

  return API_URL ? `${API_URL}/${normalized}` : normalized;
}

function toId(v: string | number) {
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const FREE_SHIP_TARGET = 500_000;

export default function MiniCartDrawer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const formatVnd = (v: number) => (v ?? 0).toLocaleString(moneyLocale) + "₫";

  const { items, isOpen, toggle, updateItem, removeItem, reloadCart } = useCart();
  const navigate = useNavigate();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const [removing, setRemoving] = useState<Record<number, boolean>>({});
  const [imgFailed, setImgFailed] = useState<Record<number, boolean>>({});

  // chống spam reloadCart khi drawer mở/đóng liên tục
  const reloadingRef = useRef(false);

  /* ======================
     LOCK SCROLL WHEN OPEN
  ====================== */
  useEffect(() => {
    if (isOpen) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const tmr = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") toggle();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(tmr);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, toggle]);

  /* ======================
     ✅ GET CART WHEN OPEN
  ====================== */
  useEffect(() => {
    if (!isOpen) return;
    if (reloadingRef.current) return;

    reloadingRef.current = true;

    Promise.resolve(reloadCart())
      .catch((e) => console.error("reloadCart failed:", e))
      .finally(() => {
        window.setTimeout(() => {
          reloadingRef.current = false;
        }, 250);
      });
  }, [isOpen, reloadCart]);

  const count = useMemo(
    () => items.reduce((s, i) => s + (Number(i.quantity) || 0), 0),
    [items],
  );

  const total = useMemo(
    () =>
      items.reduce(
        (sum, i) => sum + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0),
        0,
      ),
    [items],
  );

  const shipProgress = useMemo(() => {
    if (FREE_SHIP_TARGET <= 0) return { pct: 0, left: 0, unlocked: false };
    const pct = Math.min(100, Math.round((total / FREE_SHIP_TARGET) * 100));
    const left = Math.max(0, FREE_SHIP_TARGET - total);
    return { pct, left, unlocked: left <= 0 };
  }, [total]);

  const go = (path: string) => {
    toggle();
    navigate(path);
  };

  const safeRemove = (variantId: string | number) => {
    const id = toId(variantId);
    if (!id) return;

    setRemoving((m) => ({ ...m, [id]: true }));

    window.setTimeout(async () => {
      try {
        await removeItem(id);
      } catch (e) {
        console.error("removeItem failed:", e);
      } finally {
        setImgFailed((m) => {
          const copy = { ...m };
          delete copy[id];
          return copy;
        });

        setRemoving((m) => {
          const copy = { ...m };
          delete copy[id];
          return copy;
        });
      }
    }, 180);
  };

  const safeUpdateQty = async (variantId: string | number, nextQty: number) => {
    const id = toId(variantId);
    if (!id) return;
    try {
      await updateItem(id, nextQty);
    } catch (e) {
      console.error("updateItem failed:", e);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70]">
          {/* ================= OVERLAY ================= */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggle}
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
          />

          {/* ================= DRAWER ================= */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              "absolute right-0 top-0 h-full w-[420px] max-w-[95vw]",
              "bg-brand-ivory shadow-2xl flex flex-col",
              "border-l border-black/10",
            )}
            role="dialog"
            aria-modal="true"
            aria-label={t("cart.drawerAria")}
          >
            {/* ================= HEADER ================= */}
            <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-black/10">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.62em] text-black/45">
                    OWL DREAM
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <h3 className="text-[12px] uppercase tracking-[0.42em] text-black">
                      {t("header.cart")}
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.45em] text-black/40">
                      ({count})
                    </span>
                  </div>
                </div>

                <button
                  ref={closeBtnRef}
                  onClick={toggle}
                  className={cn(
                    "h-10 w-10 grid place-items-center rounded-full",
                    "border border-black/10 bg-white/40",
                    "hover:bg-black/5 hover:border-black/25 transition",
                    "focus:outline-none focus:ring-2 focus:ring-black/20",
                  )}
                  aria-label={t("cart.close")}
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Free shipping meter */}
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.45em] text-black/45">
                    {t("cart.freeShipping")}
                  </p>

                  {items.length > 0 ? (
                    shipProgress.unlocked ? (
                      <p className="text-[10px] uppercase tracking-[0.35em] text-black/60">
                        {t("cart.unlocked")}
                      </p>
                    ) : (
                      <p className="text-[10px] uppercase tracking-[0.35em] text-black/45">
                        {t("cart.moreToUnlock", { amount: formatVnd(shipProgress.left) })}
                      </p>
                    )
                  ) : (
                    <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                      {t("cart.addItemsToUnlock")}
                    </p>
                  )}
                </div>

                <div className="mt-2 h-[7px] rounded-full bg-black/10 overflow-hidden">
                  <div
                    className="h-full bg-brand-brown transition-[width] duration-300"
                    style={{ width: `${items.length ? shipProgress.pct : 0}%` }}
                  />
                </div>

                {items.length > 0 && (
                  <p className="mt-2 text-[9px] uppercase tracking-[0.38em] text-black/30">
                    {t("cart.addOneMorePiece")}
                  </p>
                )}
              </div>
            </div>

            {/* ================= BODY ================= */}
            <div className="flex-1 overflow-auto px-5 sm:px-6 py-5">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 rounded-2xl border border-black/10 bg-white/40 grid place-items-center">
                    <ShoppingBag className="w-5 h-5 text-black/60" />
                  </div>

                  <p className="mt-6 text-[12px] uppercase tracking-[0.45em] text-black/65">
                    {t("cart.emptyTitle")}
                  </p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.55em] text-black/35">
                    {t("cart.emptySubtitle")}
                  </p>

                  <button
                    onClick={() => go("/drops")}
                    className={cn(
                      "mt-7 h-11 px-9 rounded-full",
                      "bg-brand-brown text-brand-ivory",
                      "text-[10px] uppercase tracking-[0.55em]",
                      "hover:opacity-95 transition",
                    )}
                    type="button"
                  >
                    {t("cart.browseDrops")}
                  </button>

                  <button
                    onClick={() => go("/")}
                    className={cn(
                      "mt-3 h-11 px-9 rounded-full",
                      "border border-black/15 bg-white/30",
                      "text-[10px] uppercase tracking-[0.55em] text-black/70",
                      "hover:border-black/35 hover:bg-white/60 transition",
                    )}
                    type="button"
                  >
                    {t("cart.backHome")}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((i) => {
                    const id = toId(i.variantId);
                    const name =
                      i.productName?.trim() || t("cart.itemFallback", { id });

                    const imgSrc = imgFailed[id]
                      ? "/images/placeholder.jpg"
                      : assetUrl(i.imageUrl);

                    const unit = Number(i.unitPrice) || 0;
                    const qty = Number(i.quantity) || 0;
                    const lineTotal = unit * qty;
                    const isRemovingNow = !!removing[id];

                    const dec = async () => {
                      const next = qty - 1;
                      if (next <= 0) safeRemove(id);
                      else await safeUpdateQty(id, next);
                    };

                    const inc = async () => {
                      await safeUpdateQty(id, qty + 1);
                    };

                    return (
                      <motion.div
                        key={id}
                        layout
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        className={cn(
                          "rounded-3xl border border-black/10 bg-white/45",
                          "p-4 sm:p-5",
                          "shadow-[0_10px_35px_-28px_rgba(0,0,0,0.55)]",
                          isRemovingNow && "opacity-60",
                        )}
                      >
                        <div className="flex gap-4">
                          {/* IMAGE */}
                          <div className="h-[92px] w-[74px] overflow-hidden rounded-2xl bg-black/5 flex-shrink-0 border border-black/10">
                            <img
                              src={imgSrc}
                              alt={name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                              decoding="async"
                              onError={() => {
                                setImgFailed((m) =>
                                  m[id] ? m : { ...m, [id]: true },
                                );
                              }}
                            />
                          </div>

                          {/* INFO */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.34em] text-black truncate">
                                  {name}
                                </p>

                                {(i.color || i.size) && (
                                  <p className="mt-1 text-[10px] uppercase tracking-[0.35em] text-black/40">
                                    {i.color ? t("cart.color", { color: i.color }) : ""}
                                    {i.color && i.size ? " · " : ""}
                                    {i.size ? t("cart.size", { size: i.size }) : ""}
                                  </p>
                                )}

                                <p className="mt-2 text-[10px] uppercase tracking-[0.35em] text-black/45">
                                  {t("cart.unitEach", { price: formatVnd(unit) })}
                                </p>
                              </div>

                              <button
                                onClick={() => safeRemove(id)}
                                className={cn(
                                  "h-9 w-9 grid place-items-center rounded-full",
                                  "border border-black/10 bg-white/40",
                                  "text-black/55 hover:text-red-600 hover:border-black/25 hover:bg-black/5 transition",
                                )}
                                aria-label={t("cart.removeItem")}
                                type="button"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* QTY + LINE TOTAL */}
                            <div className="mt-4 flex items-center justify-between gap-4">
                              <div className="inline-flex items-center h-10 rounded-full border border-black/15 overflow-hidden bg-white/50">
                                <button
                                  onClick={dec}
                                  className="w-10 h-10 grid place-items-center hover:bg-black/5 active:scale-95 transition"
                                  aria-label={t("cart.decreaseQty")}
                                  type="button"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>

                                <div className="w-10 text-center text-[12px] text-black">
                                  {qty}
                                </div>

                                <button
                                  onClick={inc}
                                  className="w-10 h-10 grid place-items-center hover:bg-black/5 active:scale-95 transition"
                                  aria-label={t("cart.increaseQty")}
                                  type="button"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="text-right">
                                <p className="text-[9px] uppercase tracking-[0.45em] text-black/40">
                                  {t("cart.total")}
                                </p>
                                <p className="mt-1 text-[14px] tracking-wide text-black">
                                  {formatVnd(lineTotal)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ================= FOOTER ================= */}
            {items.length > 0 && (
              <div className="border-t border-black/10 bg-brand-ivory px-5 sm:px-6 py-5">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                      {t("cart.total")}
                    </p>
                    <p className="mt-1 text-[18px] tracking-wide text-black">
                      {formatVnd(total)}
                    </p>
                  </div>

                  <button
                    onClick={() => go("/collection")}
                    className="text-[10px] uppercase tracking-[0.45em] text-black/45 hover:text-black transition inline-flex items-center gap-2"
                    type="button"
                  >
                    {t("cart.addMore")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => go("/cart")}
                    className={cn(
                      "h-11 rounded-full",
                      "border border-black/15 bg-white/30",
                      "text-[10px] uppercase tracking-[0.55em] text-black/70",
                      "hover:bg-white/60 hover:border-black/35 transition",
                    )}
                    type="button"
                  >
                    {t("cart.viewCart")}
                  </button>

                  <button
                    onClick={() => go("/checkout")}
                    className={cn(
                      "h-11 rounded-full",
                      "bg-brand-brown text-brand-ivory",
                      "text-[10px] uppercase tracking-[0.55em]",
                      "hover:opacity-95 transition",
                    )}
                    type="button"
                  >
                    {t("cart.checkout")}
                  </button>
                </div>

                <p className="mt-4 text-[9px] uppercase tracking-[0.45em] text-black/30">
                  {t("cart.secureFast")}
                </p>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
